// ============================================================
// BANEPAD ENGINE — pure helpers extracted from the original monolith
// (Phase 3, incremental split).
//
// Pure resolver + helpers that derive what is happening on a court right now
// (and over the next hours) from the club's bookings plus the active
// tournament/series. Used by the BanepadCentralView (main screen) and
// BanepadCourtScreen (court-side pad), which now live as React components in
// src/ (a later frontend pass) along with their component-local closures
// (autoForceForCourt, courtCtx, statusOf).
//
// No storage, no React. Local date format only (NEVER toISOString) per the
// project rules (HANDOVER §13). This module must NEVER import from
// legacy/App.jsx.
//
// Functions extracted here (all exported): banepadCourtList, banepadMatchStartMin,
// banepadTimelineForCourt, banepadFindCourtMatch, banepadResolveCourt,
// banepadFormatLabel, banepadTypeTag, banepadBookingTag, banepadMatchClock,
// banepadScoreText, banepadBookingRange, banepadHourlyStatus, banepadHourEvents.
//
// banepadTournamentView (app view-routing mapping) intentionally lives in the
// frontend layer — it is a frontend concern coupled to the app's view names.
// ============================================================

import { banepadMatchStartMin, clubEffectiveCourts } from '@starpoint/engine';
import { TOURNAMENT_TYPES } from './constants';

// ── Module-private clock helpers ──
// timeToMinutes / minutesToTime were originally defined in the monolith and
// have their own copy here. They differ subtly from ../lib/date's
// toMinHHMM/fromMinHHMM (numeric-input handling and a '+1d' suffix), so to keep
// behaviour byte-for-byte identical they are copied here verbatim as private
// helpers rather than re-pointed at the date lib.

// Convert "HH:MM" to minutes since midnight
function timeToMinutes(timeStr: any): number {
  if (typeof timeStr === 'number') return timeStr * 60; // if only an hour value
  const [h, m] = timeStr.split(':').map(Number);
  return h * 60 + (m || 0);
}

function minutesToTime(min: number): string {
  const totalH = Math.floor(min / 60);
  const h = ((totalH % 24) + 24) % 24; // wrap 0-23
  const m = Math.round(min % 60);
  const next = totalH >= 24 ? ' +1d' : '';
  return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}${next}`;
}

// Whether a MANUALLY-forced event should now auto-clear back to normal, using the
// same auto-vis rules (minutesAfter / afterOnlyIfPending). Clears when:
//   1. the event is FINISHED — status 'done', or every match has a result; or
//   2. the event has bookings TODAY and we are PAST its time window (latest end +
//      "after" buffer; the buffer drops to 0 once all results are in when
//      afterOnlyIfPending is on).
// An event with no bookings today (e.g. a preview-force of a future tournament) is
// never auto-cleared — only the manual toggle turns that off.
export function banepadForceShouldClear({
  event,
  isSeries = false,
  bookings = [],
  autoForce = {},
  nowMin,
  todayISO,
}: any): boolean {
  if (!event) return false;
  const matches = event.matches || [];
  const matchDone = (m: any) => (isSeries ? !!(m.score || m.completed) : !!m.completed);
  const allDone = matches.length > 0 && matches.every(matchDone);
  if (event.status === 'done' || allDone) return true;

  const id = event.id || event.displayId;
  const todays = (bookings || []).filter((b: any) => b.date === todayISO && b.seriesId === id);
  if (!todays.length) return false; // off-window preview force → keep until manual off

  let maxEnd = -Infinity;
  todays.forEach((b: any) => {
    const s = timeToMinutes(b.startTime || '00:00');
    let e = timeToMinutes(b.endTime || b.startTime || '00:00');
    if (e <= s) e += 1440;
    if (e > maxEnd) maxEnd = e;
  });
  let after = Number(autoForce?.minutesAfter) || 0;
  if (autoForce?.afterOnlyIfPending && matches.length > 0) {
    const pending = matches.some((m: any) => !matchDone(m));
    if (!pending) after = 0;
  }
  return nowMin >= maxEnd + after;
}

// ============================================================
// BANEPAD: pure helpers (derive the court's timeline + what is happening now)
// ============================================================
// Used by BanepadCentralView (main screen) and BanepadCourtScreen (court-side
// pad). No storage, no React — derives only from bookings + active
// tournament/series. Local date format (NEVER toISOString) per the project rules.

// The pad's court numbers: the booking calendar's courts, in court order.
//
// THE ENGINE'S RULE, NOT A COPY OF IT (#873). This used to spell out
// `enabledCourts → courtCount → clubInfo.courts || 4` a second time, beside
// the engine's `clubEffectiveCourts`, which is what the calendar, the server's
// validation and MobileBook run. The two agreed for every config the booking
// settings can write (a whole `courtCount` from 1, a list of court numbers),
// which `court-count-call-sites.test.tsx` checks across a grid. They differed
// only for shapes the editor never writes (a court number stored as a string,
// a `courtCount` below 1), and there the calendar's answer is the one the pad
// should show.
//
// The SORT is the pad's own, and stays: `enabledCourts` is kept in the order
// courts were switched on, and Sentralen lists them by number.
export function banepadCourtList(bookingConfig: any, clubInfo: any): number[] {
  return [...clubEffectiveCourts(bookingConfig, clubInfo)].sort((a, b) => a - b);
}

// Moved to @starpoint/engine so the API resolves a court with the SAME code the
// browser does — Sentralen, the pad preview and the physical pad must agree
// about one court, and they only do so by running one implementation.
// Re-exported here so every existing import keeps working.
export {
  banepadMatchStartMin,
  banepadTimelineForCourt,
  banepadFindCourtMatch,
  banepadResolveCourt,
} from '@starpoint/engine';

// Readable Norwegian format name for the pad banner.
export function banepadFormatLabel(formatType: any): string {
  const map: Record<string, string> = {
    padel24: 'Padel24',
    klubbmesterskap: 'Klubbmesterskap',
    seriespill: 'Seriespill',
    americano: 'Americano',
    mexicano: 'Mexicano',
    mixicano: 'Mixicano',
    team_mexicano: 'Team Mexicano',
    mix_americano: 'Mix Americano',
    club_americano: 'Club Americano',
    club_team_americano: 'Club Team Americano',
    club_mexicano: 'Club Mexicano',
  };
  return map[formatType] || 'Turnering';
}

// Activity tag (Americano/Coaching/…) for an event → { label, abbrev, color }.
export function banepadTypeTag(typeId: any): any {
  if (!typeId) return null;
  if (typeId === 'seriespill') return { label: 'Seriespill', abbrev: 'SP', color: '#3b82f6' };
  const t = TOURNAMENT_TYPES[typeId];
  return t ? { label: t.label, abbrev: t.abbrev, color: t.color } : null;
}
// Tag for a booking: direct type, active series (seriespill), activity via seriesId,
// or a LOCAL planned single match (planned_matches_v1), which links the other way
// round — plan.bookingId → booking.id — and therefore carries no marker on the
// booking itself. Without the plan lookup an enkeltmatch booked by a booker shows
// up as a plain, untagged "Booking" on the pad.
export function banepadBookingTag(
  b: any,
  activities: any,
  activeSeries: any,
  plannedMatches: any = [],
): any {
  if (!b) return null;
  let typeId = b.tournamentType || b.activityType || null;
  if (!typeId && b.seriesId) {
    if (activeSeries && b.seriesId === activeSeries.id) return banepadTypeTag('seriespill');
    if (Array.isArray(activities)) {
      const act = activities.find((a) => a.id === b.seriesId);
      typeId = act?.config?.tournamentType || act?.tournamentType || null;
    }
  }
  if (!typeId && b.id && Array.isArray(plannedMatches)) {
    const plan = plannedMatches.find(
      (p: any) => p?.bookingId === b.id && p.status !== 'cancelled' && p.status !== 'completed',
    );
    if (plan) typeId = 'single_match';
  }
  return banepadTypeTag(typeId);
}

// HH:MM for a match's start time (both schemas).
export function banepadMatchClock(m: any): string | null {
  const s = banepadMatchStartMin(m);
  if (s == null) return null;
  const hh = String(Math.floor((s % 1440) / 60)).padStart(2, '0');
  const mm = String(s % 60).padStart(2, '0');
  return `${hh}:${mm}`;
}

// Score as text regardless of format: Padel24 stores a string ('9-5'), KM stores
// an object ({ t1, t2, sets, walkover }). Returns null if there is no score.
export function banepadScoreText(score: any): string | null {
  if (score == null) return null;
  if (typeof score === 'string' || typeof score === 'number') return String(score);
  if (typeof score === 'object') {
    if (score.walkover) return 'WO';
    if (Array.isArray(score.sets) && score.sets.length > 0) {
      return score.sets.map((s: any) => `${s.t1}-${s.t2}`).join(', ');
    }
    if (score.t1 != null && score.t2 != null) return `${score.t1}-${score.t2}`;
  }
  return null;
}

// Time-range text for a booking ('08:00–09:30').
export function banepadBookingRange(b: any): string {
  if (!b) return '';
  return `${b.startTime || ''}${b.endTime ? '–' + b.endTime : ''}`;
}

// Hour-by-hour status for the next N hours on a court → the right-hand "quick bar".
// Each hour is split into segments by the actual booked/free time (earliest on top),
// so 30 min booked yields a half/half split. Returns per hour:
//   { label, isNow, segs: [{ from, to, mins, kind }] }  kind: idle|booking|tournament
export function banepadHourlyStatus(
  court: any,
  bookings: any[],
  tournament: any,
  activeSeries: any,
  nowMin: number,
  todayISO: string,
  hours = 6,
  fromHour: number | null = null,
): any[] {
  const intervals: any[] = [];
  (bookings || [])
    .filter((b) => Number(b?.court) === Number(court) && b?.date === todayISO)
    .forEach((b) => {
      const s = timeToMinutes(b.startTime || '00:00');
      let e = timeToMinutes(b.endTime || b.startTime || '00:00');
      if (e <= s) e += 1440;
      intervals.push({ s, e, kind: 'booking' });
    });
  const addMatches = (obj: any, dur: number) => {
    if (!obj || !Array.isArray(obj.matches)) return;
    obj.matches.forEach((m: any) => {
      if (Number(m?.court) !== Number(court)) return;
      if (m?.date && m.date !== todayISO) return;
      const s = banepadMatchStartMin(m);
      if (s == null) return;
      intervals.push({ s, e: s + dur, kind: 'tournament' });
    });
  };
  addMatches(tournament, tournament?.config?.days?.[0]?.matchDuration || 30);
  addMatches(activeSeries, activeSeries?.config?.slotDuration || 90);
  // Status at the midpoint of a minute interval (tournament wins over booking).
  const kindAt = (min: number) => {
    let k = 'idle';
    for (const iv of intervals) {
      if (iv.s <= min && min < iv.e)
        k = iv.kind === 'tournament' ? 'tournament' : k === 'tournament' ? 'tournament' : 'booking';
    }
    return k;
  };
  const curHour = Math.floor(nowMin / 60);
  const startHour = fromHour != null ? fromHour : curHour;
  const out = [];
  for (let i = 0; i < hours; i++) {
    const h = startHour + i,
      hs = h * 60,
      he = hs + 60;
    // Split points within the hour = booking/match boundaries that fall inside it.
    const pts = new Set([hs, he]);
    for (const iv of intervals) {
      if (iv.s > hs && iv.s < he) pts.add(iv.s);
      if (iv.e > hs && iv.e < he) pts.add(iv.e);
    }
    const sorted = Array.from(pts).sort((a, b) => a - b);
    const segs: any[] = [];
    for (let j = 0; j < sorted.length - 1; j++) {
      const a = sorted[j],
        b = sorted[j + 1];
      const kind = kindAt((a + b) / 2);
      const last = segs[segs.length - 1];
      if (last && last.kind === kind) last.to = b;
      else segs.push({ from: a, to: b, kind });
    }
    out.push({
      label: String(h % 24).padStart(2, '0'),
      hourAbs: h,
      isNow: h === curHour,
      isPast: h < curHour,
      segs: segs.map((s) => ({ ...s, mins: s.to - s.from })),
    });
  }
  return out;
}

// Events (booking/tournament/series) that overlap a selected hour on the court.
// Used when an hour in the quick bar is clicked — shows both at a match changeover.
export function banepadHourEvents(
  court: any,
  bookings: any[],
  tournament: any,
  activeSeries: any,
  hourAbs: number,
  todayISO: string,
): any[] {
  const hs = hourAbs * 60,
    he = hs + 60;
  const fmt = (min: number) => minutesToTime(((min % 1440) + 1440) % 1440);
  const out: any[] = [];
  (bookings || [])
    .filter((b) => Number(b?.court) === Number(court) && b?.date === todayISO)
    .forEach((b) => {
      const s = timeToMinutes(b.startTime || '00:00');
      let e = timeToMinutes(b.endTime || b.startTime || '00:00');
      if (e <= s) e += 1440;
      if (s < he && e > hs)
        out.push({
          kind: 'booking',
          name: b.bookedBy?.name || b.title || 'Reservert',
          s,
          e,
          range: `${fmt(s)}–${fmt(e)}`,
          notes: b.notes,
          seriesId: b.seriesId,
          tournamentType: b.tournamentType,
        });
    });
  const addM = (obj: any, dur: number, fallbackType: string) => {
    if (!obj || !Array.isArray(obj.matches)) return;
    obj.matches.forEach((m: any) => {
      if (Number(m?.court) !== Number(court)) return;
      if (m?.date && m.date !== todayISO) return;
      const s = banepadMatchStartMin(m);
      if (s == null) return;
      const e = s + dur;
      if (s < he && e > hs)
        out.push({
          kind: 'tournament',
          formatType: obj.type || fallbackType,
          name: obj.name || obj.config?.name || 'Turnering',
          s,
          e,
          range: `${fmt(s)}–${fmt(e)}`,
          score: m.score,
          completed: m.completed,
        });
    });
  };
  addM(tournament, tournament?.config?.days?.[0]?.matchDuration || 30, 'padel24');
  addM(activeSeries, activeSeries?.config?.slotDuration || 90, 'seriespill');
  return out.sort((a, b) => a.s - b.s);
}
